'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface MediaCarouselProps {
	images: {
		url?: string;
		alternativeText?: string;
		ext?: string;
	}[];
	lazyLoad?: boolean;
}

// Utility function to determine media type
const getMediaType = (url: string, ext: string) => {
	// Check extension first if available
	if (ext) {
		const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
		return videoExtensions.includes(ext.toLowerCase()) ? 'video' : 'image';
	}

	// Fallback to URL check if extension is not available
	if (url) {
		const videoExtensions = /\.(mp4|webm|ogg|mov)$/i;
		return videoExtensions.test(url) ? 'video' : 'image';
	}

	// Default to image if no information is available
	return 'image';
};

export default function MediaCarousel({
	images,
	lazyLoad = true,
}: MediaCarouselProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isVisible, setIsVisible] = useState(false);
	const carouselRef = useRef<HTMLDivElement>(null);

	// Don't render carousel if only one image
	if (images.length <= 1) {
		return images.length === 1 && images[0]?.url ? (
			<div
				className="relative h-[280px] w-full overflow-hidden sm:h-[350px] md:h-[400px] lg:h-[500px]"
				ref={carouselRef}>
				{getMediaType(images[0].url, images[0].ext || '') ===
				'video' ? (
					<video
						src={images[0].url}
						className="h-full w-full object-contain"
						preload={lazyLoad ? 'none' : 'auto'}
						playsInline
						muted
						autoPlay
						loop
						controls
					/>
				) : (
					<Image
						src={images[0].url}
						alt={images[0].alternativeText || 'Carousel image'}
						fill
						className="aspect-video object-contain"
						sizes="(max-width: 640px) 100vw, (max-width: 768px) 75vw, (max-width: 1024px) 50vw, 33vw"
						priority={!lazyLoad}
					/>
				)}
			</div>
		) : null;
	}

	// Use Intersection Observer to detect when carousel is visible
	useEffect(() => {
		if (!carouselRef.current) return;

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 }, // Trigger when 10% of the element is visible
		);

		observer.observe(carouselRef.current);

		return () => {
			observer.disconnect();
		};
	}, []);

	const nextSlide = () => {
		setCurrentIndex(prevIndex =>
			prevIndex === images.length - 1 ? 0 : prevIndex + 1,
		);
	};

	const prevSlide = () => {
		setCurrentIndex(prevIndex =>
			prevIndex === 0 ? images.length - 1 : prevIndex - 1,
		);
	};

	// Auto-advance slides every 5 seconds only when visible
	useEffect(() => {
		if (!isVisible) return;

		const timer = setInterval(nextSlide, 7000);
		return () => clearInterval(timer);
	}, [isVisible]);

	return (
		<div className="group relative w-full" ref={carouselRef}>
			<div className="relative h-[280px] overflow-hidden sm:h-[350px] md:h-[400px] lg:h-[500px]">
				{images.map((item, index) => {
					// Defer offscreen carousel media until the block enters the viewport
					const shouldRender =
						(!lazyLoad || isVisible) &&
						(index === 0 ||
							index === currentIndex ||
							Math.abs(index - currentIndex) === 1);

					return (
						<div
							key={index}
							className={cn(
								'absolute inset-0 transform transition-all duration-700 ease-in-out',
								index === currentIndex
									? 'translate-x-0 opacity-100'
									: index < currentIndex
										? '-translate-x-full opacity-0'
										: 'translate-x-full opacity-0',
							)}>
							{item.url &&
								shouldRender &&
								(getMediaType(item.url, item.ext || '') ===
								'video' ? (
									<video
										src={item.url}
										className="h-full w-full object-contain"
										preload={lazyLoad ? 'none' : 'auto'}
										playsInline
										muted
										autoPlay
										loop
										controls
									/>
								) : (
									<Image
										src={item.url}
										alt={
											item.alternativeText ||
											'Carousel image'
										}
										fill
										className="object-contain"
										sizes="(max-width: 640px) 100vw, (max-width: 768px) 75vw, (max-width: 1024px) 50vw, 33vw"
										priority={index === 0 && !lazyLoad}
										loading={
											lazyLoad || index > 0
												? 'lazy'
												: 'eager'
										}
										placeholder="blur"
										blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
										fetchPriority={
											index === 0 && !lazyLoad
												? 'high'
												: 'auto'
										}
									/>
								))}
						</div>
					);
				})}
			</div>

			{/* Navigation Buttons */}
			<div className="absolute inset-0 flex items-center justify-between">
				<button
					onClick={prevSlide}
					className={cn(
						'rounded-full bg-white/80 p-1 shadow-lg backdrop-blur-sm sm:p-2',
						'transform transition-all duration-300',
						'opacity-0 hover:bg-white group-hover:opacity-100',
						'hover:scale-110 active:scale-95',
					)}
					aria-label="Previous slide">
					<svg
						className="h-4 w-4 text-gray-800 sm:h-6 sm:w-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
				</button>
				<button
					onClick={nextSlide}
					className={cn(
						'rounded-full bg-white/80 p-1 shadow-lg backdrop-blur-sm sm:p-2',
						'transform transition-all duration-300',
						'opacity-0 hover:bg-white group-hover:opacity-100',
						'hover:scale-110 active:scale-95',
					)}
					aria-label="Next slide">
					<svg
						className="h-4 w-4 text-gray-800 sm:h-6 sm:w-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			</div>

			{/* Dots Indicator */}
			<div className="absolute -bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-1 sm:gap-2">
				{images.map((_, index) => (
					<button
						key={index}
						onClick={() => setCurrentIndex(index)}
						className={cn(
							'transform transition-all duration-300',
							'hover:scale-110 active:scale-95',
							index === currentIndex
								? 'h-1.5 w-6 rounded-full bg-blue-600 sm:h-2 sm:w-8'
								: 'h-1.5 w-1.5 rounded-full bg-gray-300 hover:bg-gray-400 sm:h-2 sm:w-2',
						)}
						aria-label={`Go to slide ${index + 1}`}
					/>
				))}
			</div>
		</div>
	);
}
